import { AssetClass, LoanField } from '__generated__/globalTypes';
type FieldConfig = {
  field: LoanField;
  label: string;
  fallback?: LoanField;
};

const MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  // An FDIC tape always keys on its 4C Asset ID column, never the bank's own loan number.
  { field: LoanField.account_id, label: 'Account Id (4C Asset ID)' },
  {
    field: LoanField.current_balance_cents,
    label: 'Current Balance',
    // Backend reconstructs current_balance from original_balance for any row
    // whose first_payment_date is strictly after today (or whose I/O period
    // is still active). Rows that don't qualify will be reported per-row in
    // the processing report after ingest. See ENG-2806/ENG-2807.
    fallback: LoanField.original_balance_cents,
  },
  { field: LoanField.interest_rate_bps, label: 'Interest Rate' },
  {
    field: LoanField.maturity_date_seconds,
    label: 'Maturity Date',
    fallback: LoanField.remaining_loan_terms_months,
    // Note: loan_terms_months is also accepted as a fallback (handled in validation logic)
  },
  {
    field: LoanField.original_balance_cents,
    label: 'Original Balance',
    // Mirror direction: see current_balance_cents above.
    fallback: LoanField.current_balance_cents,
  },
  { field: LoanField.first_payment_date_seconds, label: 'First Payment Date' },
];

const CONSUMER_MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.borrower_credit_score, label: 'Borrower Credit Score' },
  { field: LoanField.dti_bps, label: 'Debt to Income Ratio' },
];

// Asset-depletion flag (ENG-3391): optional on every consumer class. Truthy
// (1/true/yes) authorizes a blank DTI on that row and the loan classifies as
// underwriting_type ASSET_UTIL. Deliberately NOT in a *_MANDATORY list — the
// mapping gate requires every mandatory field, which would stall tapes
// without the column (same trap the api's REQUIRED_FIELDS spreads had).
const CONSUMER_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.asset_based_decision, label: 'Asset Based Decision' },
];

const SECURED_CONSUMER_MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  ...CONSUMER_MANDATORY_LOAN_FIELDS,
  { field: LoanField.ltv_bps, label: 'LTV' },
  { field: LoanField.state, label: 'State' },
];

const UNSECURED_CUSTOMER_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.purpose, label: 'Loan Purpose' },
  { field: LoanField.state, label: 'State' },
];

const AUTO_MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.property_year, label: 'Vehicle Year' },
];

const AUTO_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.auto_vin, label: 'Vehicle VIN' },
  {
    field: LoanField.auto_original_value_cents,
    label: 'Vehicle Original Value',
  },
];

const HOME_MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.purpose, label: 'Loan Purpose' },
  { field: LoanField.occupancy, label: 'Occupancy Type' },
];

const HOME_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.property_year, label: 'Property Year' },
  { field: LoanField.cra, label: 'CRA' },
  // DSCR / Non-QM subtype mapping (ENG-2841). qm_status is the seller's Non-QM
  // flag column (interpreted per the declared upload mode in the Validate
  // classifier, ENG-2828); dscr_bps / rent_type are auto-mapped from common
  // headers but also manually mappable for cryptic ones.
  { field: LoanField.qm_status, label: 'Non-QM Flag' },
  { field: LoanField.dscr_bps, label: 'DSCR' },
  { field: LoanField.rent_type, label: 'Rent Type' },
];

const HOME_ARM_MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  {
    field: LoanField.first_rate_change_date_seconds,
    label: 'First Rate Change Date',
  },
  {
    field: LoanField.rap_months,
    label: 'Rate Adjustment Period (RAP) (Months)',
  },
];

const CRE_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.ltv_bps, label: 'LTV' },
  { field: LoanField.property_description, label: 'Property Description' },
  { field: LoanField.property_year, label: 'Property Year' },
  { field: LoanField.occupancy, label: 'Occupancy' },
  { field: LoanField.purpose, label: 'Purpose' },
  { field: LoanField.cra, label: 'CRA' },
];

const HELOC_MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.credit_line_amount_cents, label: 'Credit Line Amount' },
  { field: LoanField.draw_period_months, label: 'Draw Period (Months)' },
  { field: LoanField.lien_position, label: 'Lien Position' },
];

const HELOC_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.repayment_period_months, label: 'Repayment Period (Months)' },
  { field: LoanField.draw_start_date_seconds, label: 'Draw Start Date' },
  { field: LoanField.combined_ltv_bps, label: 'Combined LTV (CLTV)' },
];

// Boat Year → property_year. Required to match the api MARINE tape config, where
// property_year is mandatory (via AUTO_LOAN_MANDATORY). Was omitted from the marine
// config, so the column couldn't be mapped and was silently dropped (ENG-3084).
const MARINE_MANDATORY_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.property_year, label: 'Boat Year' },
];

// Marine capture-only fields (ENG-2961). All optional at tape time.
// Boat Make / Boat Model are dedicated fields (ENG-3515): tapes carry them as
// two columns, which can't share one mapped field like property_description.
const MARINE_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.charter, label: 'Charter' },
  { field: LoanField.dealer_reserve_cents, label: 'Dealer Reserve' },
  { field: LoanField.dealer_reserve_bps, label: 'Dealer Reserve (bps)' },
  { field: LoanField.boat_make, label: 'Boat Make' },
  { field: LoanField.boat_model, label: 'Boat Model' },
];

const HOME_ARM_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.arm_margin_bps, label: 'ARM Margin' },
  { field: LoanField.arm_index, label: 'ARM Index' },
  { field: LoanField.arm_index_rate_bps, label: 'ARM Index Rate' },
  {
    field: LoanField.irap_months,
    label: 'Initial Rate Adjustment Period (IRAP) (Months)',
  },
  { field: LoanField.interest_only, label: 'Interest Only (I/O)' },
  { field: LoanField.io_period_months, label: 'IO Period (Months)' },
  { field: LoanField.lifetime_floor_rate_bps, label: 'Lifetime Rate Floor' },
  { field: LoanField.lifetime_cap_rate_bps, label: 'Lifetime Rate Cap' },
  { field: LoanField.periodic_cap_rate_bps, label: 'Periodic Rate Cap' },
  {
    field: LoanField.amortization_term_months,
    label: 'Amortization Term (Months)',
  },
  { field: LoanField.balloon_term_months, label: 'Balloon Term (Months)' },
];

// Optional to match api's ALL_LOANS_OPTIONAL: a mandatory entry would stall every tape without
// the column at the mapping gate. performance_status is enforced later, when a sale is created.
// loan_number is the bank's own number, which can repeat across the failed banks of one portfolio.
const FDIC_SALE_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.sale_key, label: 'Sale Key' },
  { field: LoanField.fund_number, label: 'Fund Number' },
  { field: LoanField.institution_id, label: 'Institution ID' },
  { field: LoanField.loan_number, label: 'Loan Number (Bank)' },
  { field: LoanField.performance_status, label: 'Performance Status' },
];

const OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  ...FDIC_SALE_OPTIONAL_LOAN_FIELDS,
  { field: LoanField.loan_terms_months, label: 'Total Loan Terms' },
  { field: LoanField.origination_date_seconds, label: 'Origination Date' },
  { field: LoanField.product, label: 'Product' },
  { field: LoanField.next_payment_date_seconds, label: 'Next Payment Date' },
  {
    field: LoanField.remaining_loan_terms_months,
    label: 'Remaining Loan Terms',
  },
  { field: LoanField.servicing_rate_bps, label: 'Servicing Rate' },
  { field: LoanField.city, label: 'City' },
  { field: LoanField.postal_code, label: 'Postal Code' },
];

const SECURED_CONSUMER_OPTIONAL_LOAN_FIELDS: FieldConfig[] = [
  { field: LoanField.property_description, label: 'Property Description' },
];

function configSort(configA: FieldConfig, configB: FieldConfig) {
  return configA.label.localeCompare(configB.label);
}

// Pre-populate every codegen'd AssetClass value with an empty entry so the
// map satisfies its mapped-type shape regardless of which asset classes are
// in the schema for the stage being built. Explicit configs below override
// the empty defaults for the classes we actually field-map. New asset
// classes added on the api side (e.g. HELOC) get a no-op entry without
// breaking other stages — matches the dynamic-defaults pattern used in
// CarveContext for the same reason.
type FieldConfigEntry = {
  requiredFields: FieldConfig[];
  optionalLoanFields: FieldConfig[];
};
// Return a fresh object per call so default entries don't alias each other —
// any consumer that mutates the arrays (e.g. push) would otherwise corrupt
// every default entry sharing the same reference.
const emptyFieldConfigEntry = (): FieldConfigEntry => ({
  requiredFields: [],
  optionalLoanFields: [],
});

const FIELD_CONFIG: { [k in AssetClass | 'HOME_ARM']: FieldConfigEntry } = {
  ...(Object.fromEntries(
    Object.values(AssetClass).map((ac) => [ac, emptyFieldConfigEntry()]),
  ) as Record<AssetClass, FieldConfigEntry>),
  [AssetClass.AUTO]: {
    requiredFields: [
      ...MANDATORY_LOAN_FIELDS,
      ...SECURED_CONSUMER_MANDATORY_LOAN_FIELDS,
      ...AUTO_MANDATORY_LOAN_FIELDS,
    ].sort(configSort),
    optionalLoanFields: [
      ...OPTIONAL_LOAN_FIELDS,
      ...AUTO_OPTIONAL_LOAN_FIELDS,
      ...SECURED_CONSUMER_OPTIONAL_LOAN_FIELDS,
      ...CONSUMER_OPTIONAL_LOAN_FIELDS,
    ].sort(configSort),
  },
  [AssetClass.UNSECPERSONAL]: {
    requiredFields: [...MANDATORY_LOAN_FIELDS, ...CONSUMER_MANDATORY_LOAN_FIELDS].sort(configSort),
    optionalLoanFields: [
      ...OPTIONAL_LOAN_FIELDS,
      ...UNSECURED_CUSTOMER_OPTIONAL_LOAN_FIELDS,
      ...CONSUMER_OPTIONAL_LOAN_FIELDS,
    ].sort(configSort),
  },
  [AssetClass.HOME]: {
    requiredFields: [
      ...MANDATORY_LOAN_FIELDS,
      ...SECURED_CONSUMER_MANDATORY_LOAN_FIELDS,
      ...HOME_MANDATORY_LOAN_FIELDS,
    ].sort(configSort),
    optionalLoanFields: [
      ...OPTIONAL_LOAN_FIELDS,
      ...HOME_OPTIONAL_LOAN_FIELDS,
      ...SECURED_CONSUMER_OPTIONAL_LOAN_FIELDS,
      ...CONSUMER_OPTIONAL_LOAN_FIELDS,
    ].sort(configSort),
  },
  HOME_ARM: {
    requiredFields: [
      ...MANDATORY_LOAN_FIELDS,
      ...SECURED_CONSUMER_MANDATORY_LOAN_FIELDS,
      ...HOME_MANDATORY_LOAN_FIELDS,
      ...HOME_ARM_MANDATORY_LOAN_FIELDS,
    ].sort(configSort),
    optionalLoanFields: [
      ...OPTIONAL_LOAN_FIELDS,
      ...HOME_OPTIONAL_LOAN_FIELDS,
      ...SECURED_CONSUMER_OPTIONAL_LOAN_FIELDS,
      ...HOME_ARM_OPTIONAL_LOAN_FIELDS,
      ...CONSUMER_OPTIONAL_LOAN_FIELDS,
    ].sort(configSort),
  },
  [AssetClass.BOAT]: {
    requiredFields: [...MANDATORY_LOAN_FIELDS],
    optionalLoanFields: [],
  },
  [AssetClass.RV]: {
    requiredFields: [...MANDATORY_LOAN_FIELDS],
    optionalLoanFields: [],
  },
  [AssetClass.CRE]: {
    requiredFields: [...MANDATORY_LOAN_FIELDS],
    optionalLoanFields: [...OPTIONAL_LOAN_FIELDS, ...CRE_OPTIONAL_LOAN_FIELDS],
  },
  [AssetClass.HELOC]: {
    requiredFields: [
      ...MANDATORY_LOAN_FIELDS,
      ...SECURED_CONSUMER_MANDATORY_LOAN_FIELDS,
      ...HELOC_MANDATORY_LOAN_FIELDS,
    ].sort(configSort),
    optionalLoanFields: [
      ...OPTIONAL_LOAN_FIELDS,
      ...HELOC_OPTIONAL_LOAN_FIELDS,
      ...CONSUMER_OPTIONAL_LOAN_FIELDS,
    ].sort(configSort),
  },
  [AssetClass.MARINE]: {
    requiredFields: [
      ...MANDATORY_LOAN_FIELDS,
      ...SECURED_CONSUMER_MANDATORY_LOAN_FIELDS,
      ...MARINE_MANDATORY_LOAN_FIELDS,
    ].sort(configSort),
    optionalLoanFields: [
      ...OPTIONAL_LOAN_FIELDS,
      ...SECURED_CONSUMER_OPTIONAL_LOAN_FIELDS,
      ...MARINE_OPTIONAL_LOAN_FIELDS,
      ...CONSUMER_OPTIONAL_LOAN_FIELDS,
    ].sort(configSort),
  },
  [AssetClass.MBL]: {
    requiredFields: [...MANDATORY_LOAN_FIELDS],
    optionalLoanFields: [],
  },
};

export { FIELD_CONFIG };
export type { FieldConfig };
